import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { RefreshCw } from 'lucide-react'

import type { Camera } from './camera-types'
import type { CameraTestResult } from '@/lib/api/cameras-api'
import type { AreaCameraFormValues } from '@/lib/schemas/grower-camera-schemas'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import {
    createAreaCameraSchema,
    extractCameraToken,
} from '@/lib/schemas/grower-camera-schemas'
import { m } from '@/lib/paraglide/messages'
import { twJoin } from '@/lib/utils'

interface Props {
    open: boolean
    /** null = thêm mới; có giá trị = sửa camera đó. */
    camera: Camera | null
    isSaving: boolean
    isTesting: boolean
    testResult: CameraTestResult | null
    onSave: (values: AreaCameraFormValues) => Promise<unknown>
    onTest: () => void
    onClose: () => void
}

const EMPTY: AreaCameraFormValues = {
    name: '',
    stream_token: '',
    control_token: '',
}

/**
 * Hộp thoại thêm/sửa một camera của vùng trồng.
 *
 * Chỉ nhập TOKEN của từng camera: khuôn đường dẫn nằm trong code backend, base
 * URL và tài khoản ở thẻ "Camera hộ trồng" màn Cấu hình SmartLab. Dán nguyên
 * link được bàn giao cũng được — ô tự tách lấy token.
 */
export function CameraFormDialog({
    open,
    camera,
    isSaving,
    isTesting,
    testResult,
    onSave,
    onTest,
    onClose,
}: Props) {
    const form = useForm<AreaCameraFormValues>({
        resolver: zodResolver(createAreaCameraSchema()),
        defaultValues: EMPTY,
    })

    useEffect(() => {
        if (!open) return
        form.reset(
            camera
                ? {
                      name: camera.name,
                      stream_token: camera.stream_token,
                      control_token: camera.control_token,
                  }
                : EMPTY,
        )
    }, [open, camera, form])

    // Lỗi lưu đã được hook toast; bắt lại ở đây để promise của handleSubmit
    // không reject ra ngoài (react-hook-form ném lại lỗi của callback, và
    // `void handleSubmit(...)` thì không ai bắt → unhandled rejection).
    const watchedStream = form.watch('stream_token')
    const watchedControl = form.watch('control_token')

    const handleSubmit = form.handleSubmit(async (values) => {
        try {
            await onSave(values)
        } catch {
            // giữ hộp thoại mở để người dùng sửa rồi lưu lại
        }
    })

    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                if (!next) onClose()
            }}
        >
            <DialogContent className="sm:max-w-xl">
                <DialogHeader>
                    <DialogTitle>
                        {camera
                            ? m.grower_area_camera_dialog_edit_title()
                            : m.grower_area_camera_dialog_add_title()}
                    </DialogTitle>
                    <p className="text-sm text-muted-foreground">
                        {m.grower_area_camera_dialog_desc()}
                    </p>
                </DialogHeader>

                <FormProvider {...form}>
                    {/* stopPropagation: hộp thoại này nằm trong form sửa hộ
                        trồng. Radix render qua portal nên DOM tách rời, nhưng
                        event React vẫn nổi lên form cha → bấm "Lưu camera" sẽ
                        submit luôn cả hộ. */}
                    <form
                        noValidate
                        id="area-camera-form"
                        className="space-y-4"
                        onSubmit={(event) => {
                            event.stopPropagation()
                            void handleSubmit(event)
                        }}
                    >
                        <Field
                            name="name"
                            label={m.grower_area_camera_field_name()}
                            required
                        >
                            <Input
                                id="name"
                                autoFocus
                                {...form.register('name')}
                            />
                        </Field>
                        <p className="-mt-2 text-xs text-muted-foreground">
                            {m.grower_area_camera_field_name_hint()}
                        </p>

                        <TokenField
                            name="stream_token"
                            label={m.grower_area_camera_field_stream()}
                            hint={m.grower_area_camera_field_stream_hint()}
                            form={form}
                        />

                        <TokenField
                            name="control_token"
                            label={m.grower_area_camera_field_control()}
                            hint={m.grower_area_camera_field_control_hint()}
                            form={form}
                        />

                        {camera && (
                            <CheckResults
                                result={testResult}
                                isTesting={isTesting}
                                // Kết quả kiểm tra thuộc về token ĐANG LƯU.
                                // Người dùng vừa sửa ô token mà vẫn thấy kết
                                // quả cũ thì sẽ tưởng token mới đã được kiểm
                                // — đúng bước nghiệm thu sai nhất.
                                stale={
                                    extractCameraToken(watchedStream) !==
                                        camera.stream_token ||
                                    extractCameraToken(watchedControl) !==
                                        camera.control_token
                                }
                            />
                        )}
                    </form>
                </FormProvider>

                <DialogFooter className="gap-2 sm:justify-between">
                    {camera ? (
                        <Button
                            type="button"
                            variant="ghost"
                            disabled={isTesting}
                            onClick={onTest}
                        >
                            <RefreshCw
                                className={twJoin(
                                    'size-4',
                                    isTesting && 'animate-spin',
                                )}
                            />
                            {isTesting
                                ? m.grower_area_camera_check_running()
                                : m.grower_area_camera_check_connection()}
                        </Button>
                    ) : (
                        <span />
                    )}
                    <div className="flex gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                        >
                            {m.common_cancel()}
                        </Button>
                        {/* Không disable theo validate — chỉ khi đang gửi. */}
                        <Button
                            type="submit"
                            form="area-camera-form"
                            disabled={isSaving}
                        >
                            {isSaving
                                ? m.common_saving()
                                : m.grower_area_camera_save()}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

function TokenField({
    name,
    label,
    hint,
    form,
}: {
    name: 'stream_token' | 'control_token'
    label: string
    hint: string
    form: ReturnType<typeof useForm<AreaCameraFormValues>>
}) {
    return (
        <div>
            <Field name={name} label={label}>
                <Input
                    id={name}
                    spellCheck={false}
                    autoComplete="off"
                    placeholder={m.grower_area_camera_token_placeholder()}
                    className="font-mono text-[13px]"
                    {...form.register(name)}
                    // Dán nguyên link camera: tách token ngay để người dùng
                    // THẤY giá trị sẽ được lưu, thay vì lưu xong mới biết.
                    onPaste={(event) => {
                        const pasted = event.clipboardData.getData('text')
                        const token = extractCameraToken(pasted)
                        if (token === pasted) return
                        event.preventDefault()
                        form.setValue(name, token, { shouldDirty: true })
                    }}
                />
            </Field>
            <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
        </div>
    )
}

function CheckResults({
    result,
    isTesting,
    stale,
}: {
    result: CameraTestResult | null
    isTesting: boolean
    stale: boolean
}) {
    if (!result && !isTesting) return null
    if (stale && !isTesting) {
        return (
            <p className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
                {m.grower_area_camera_check_stale()}
            </p>
        )
    }
    return (
        <div className="space-y-1.5 rounded-md border bg-muted/40 p-3">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {m.grower_area_camera_check_result()}
            </span>
            {isTesting && !result ? (
                <p className="text-sm text-muted-foreground">
                    {m.grower_area_camera_check_running()}
                </p>
            ) : (
                result && (
                    <div className="space-y-1">
                        <CheckLine
                            label={m.grower_area_camera_check_stream_label()}
                            ok={result.stream.ok}
                            message={result.stream.message}
                        />
                        <CheckLine
                            label={m.grower_area_camera_check_control_label()}
                            ok={result.control.ok}
                            message={result.control.message}
                        />
                    </div>
                )
            )}
            <p className="text-xs text-muted-foreground">
                {m.grower_area_camera_check_hint()}
            </p>
        </div>
    )
}

function CheckLine({
    label,
    ok,
    message,
}: {
    label: string
    ok: boolean
    message: string
}) {
    return (
        <p className="text-sm">
            <span className="font-medium">{label}: </span>
            <span
                className={twJoin(
                    ok ? 'text-secondary-foreground' : 'text-destructive',
                )}
            >
                {message}
            </span>
        </p>
    )
}
